import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map as _map, initial, split, toInteger } from 'lodash';
import { catchError, filter, map, merge, of, switchMap, tap } from 'rxjs';

import { Folder } from '@lockton-apps/shared/interfaces';
import { ClientDocumentsActions } from '@lockton-apps/clients/actions';
import { DashboardContactActions } from '@lockton-apps/dashboard/action';
import { FoldersApiActions } from '@lockton-apps/shared/action/folders';
import {
  ClientRouterSelectors,
  selectUrl,
} from '@lockton-apps/shared/data-access/router';
import { DocumentsUploadDialogService } from '@lockton-apps/shared/ui/document-upload-dialog';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';

import { FoldersService } from '../services/folders.service';
import { selectRoutedFolder } from './folders.selectors';
import { PolicyDetailsActions } from '@lockton-apps/policies/action';
import { PoliciesApiActions } from '@lockton-apps/shared/action/policies';
import { ProposalStepActions } from '@lockton-apps/proposals/action';
import { PoliciesSelectors } from '@lockton-apps/shared/data-access/policies';

@Injectable()
export class FoldersEffects {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private foldersService = inject(FoldersService);
  private feedbackService = inject(FeedbackService);
  private router = inject(Router);
  private uploadDialogService = inject(DocumentsUploadDialogService);

  loadClientFolders$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(ClientDocumentsActions.init, ProposalStepActions.init),
        concatLatestFrom(() =>
          this.store.select(ClientRouterSelectors.selectRoutedId)
        ),
        map(([, clientId]) => clientId)
      ),
      this.actions$.pipe(
        ofType(DashboardContactActions.documentsClientSelected),
        map(({ clientId }) => clientId)
      ),
      this.actions$.pipe(
        ofType(PolicyDetailsActions.init),
        switchMap(() =>
          this.actions$.pipe(
            ofType(PoliciesApiActions.loadPolicyForPolicyDetailsSuccess),
            map(({ policy }) => policy.policyClients[0].id)
          )
        )
      )
    ).pipe(
      switchMap((clientId) => {
        if (!clientId) {
          throw new Error('No Client Id');
        }
        return this.foldersService.allForClient(toInteger(clientId)).pipe(
          map((res) =>
            FoldersApiActions.clientFoldersLoadedSuccess({
              folders: res,
            })
          ),
          catchError((error) =>
            of(FoldersApiActions.clientFoldersLoadedFailure({ error }))
          )
        );
      }),
      catchError((error) =>
        of(FoldersApiActions.clientFoldersLoadedFailure({ error }))
      )
    )
  );

  addFolder$ = createEffect(() =>
    merge(
      merge(
        this.uploadDialogService.folderAndDocumentsSubmitted$(),
        this.actions$.pipe(ofType(ClientDocumentsActions.folderCreated))
      ).pipe(
        concatLatestFrom(() => [
          this.store.select(ClientRouterSelectors.selectRoutedId),
          this.store.select(selectRoutedFolder),
        ]),
        map(([{ folderName, documents }, clientId, parentFolder]) => ({
          folderName,
          documents,
          clientId,
          parentFolderId: parentFolder?.id,
        }))
      ),
      this.actions$.pipe(
        ofType(PolicyDetailsActions.folderCreated),
        concatLatestFrom(() =>
          this.store
            .select(PoliciesSelectors.selectRoutedPolicy)
            .pipe(filter(Boolean))
        ),
        map(([{ folderName, parentFolderId }, policy]) => ({
          folderName,
          parentFolderId,
          clientId: policy.policyClients[0].id,
          documents: [],
        }))
      )
    ).pipe(
      filter(({ clientId, parentFolderId }) => !!clientId && !!parentFolderId),
      switchMap(({ folderName, clientId, parentFolderId, documents }) =>
        this.foldersService
          .add({
            name: folderName,
            clientId: clientId as number,
            parentFolderId: parentFolderId as number,
          })
          .pipe(
            map((folder) =>
              FoldersApiActions.addedSuccess({
                folder,
                documents: _map(documents, (d) => ({
                  ...d,
                  documentFolderId: folder.id,
                })),
              })
            )
          )
      )
    )
  );

  addFolderSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FoldersApiActions.addedSuccess),
        tap(({ folder }) =>
          this.feedbackService.subtleMessage(
            $localize`${folder.name} created successfully`,
            {
              type: 'success',
            }
          )
        )
      ),
    { dispatch: false }
  );

  navigateToFolderIfUploadedFolderHasDocuments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FoldersApiActions.addedSuccess),
        filter(({ documents }) => !!documents?.length),
        concatLatestFrom(() => this.store.select(selectUrl)),
        tap(([{ folder }, url]) => {
          const path = initial(split(url, '/'));
          this.router.navigate([...path, folder.id]);
        })
      ),
    { dispatch: false }
  );

  updateFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientDocumentsActions.folderUpdated,
        PolicyDetailsActions.folderEditted
      ),
      switchMap(({ folder }) => {
        return this.foldersService.update(folder).pipe(
          map(() => FoldersApiActions.updatedSuccess()),
          catchError(() => of(FoldersApiActions.updatedFailure()))
        );
      })
    )
  );

  deleteFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientDocumentsActions.folderDeleted,
        PolicyDetailsActions.folderDeleted
      ),
      switchMap(({ id }) => {
        return this.foldersService.delete(id).pipe(
          map(() => FoldersApiActions.deletedSuccess()),
          catchError(() => of(FoldersApiActions.deletedFailure()))
        );
      })
    )
  );

  deleteFolderSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FoldersApiActions.deletedSuccess),
        tap(() =>
          this.feedbackService.subtleMessage(
            $localize`The folder has been deleted`,
            {
              type: 'success',
            }
          )
        )
      ),
    { dispatch: false }
  );

  deleteFolderFailureMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FoldersApiActions.deletedFailure),
        tap(() =>
          this.feedbackService.subtleMessage(
            $localize`You may not delete this folder as it is not empty. Please remove all files and subfolders from the folder before attempting to delete it.`,
            { type: 'error' }
          )
        )
      ),
    { dispatch: false }
  );

  updateFolderSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FoldersApiActions.updatedSuccess),
        tap(() =>
          this.feedbackService.subtleMessage(
            $localize`The folder has been updated`,
            {
              type: 'success',
            }
          )
        )
      ),
    { dispatch: false }
  );
}
